// Audit run — the checklist runner. Sectioned items with Pass / Fail /
// Blocked per item + a note box; every result persists server-side
// immediately, so a run survives multiple sittings. Failing an item files
// a linked QC case (the API does it) — the case chip appears inline.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { QcAuditResult, QcAuditRunDetail, QcAuditRunItem } from '@itms-qc/shared';
import { auditApi } from '../lib/api';
import { useApiToken } from '../auth/useApiToken';
import { useMe } from '../lib/me';
import { personLabel, relativeTime } from '../lib/format';

const RESULT_STYLES: Record<QcAuditResult, { bg: string; fg: string; label: string }> = {
  pass: { bg: '#E2F3E9', fg: '#0B6B3A', label: 'Pass' },
  fail: { bg: '#FED7D7', fg: '#C53030', label: 'Fail' },
  blocked: { bg: '#FEEBC8', fg: '#B7791F', label: 'Blocked' },
};

function ItemRow({
  item,
  disabled,
  onResult,
}: {
  item: QcAuditRunItem;
  disabled: boolean;
  onResult: (item: QcAuditRunItem, result: QcAuditResult | null, note: string) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState(item.note ?? '');
  const [saving, setSaving] = useState(false);

  // A fail needs an observation; open the row when the user picks fail so
  // the note box is in front of them.
  async function pick(result: QcAuditResult | null) {
    if (disabled || saving) return;
    if (result === 'fail' && !expanded) setExpanded(true);
    setSaving(true);
    try {
      await onResult(item, result, note);
    } finally {
      setSaving(false);
    }
  }

  const current = item.result;

  return (
    <div style={{ borderTop: '1px solid #EDF2F7', padding: '10px 0' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <button
          onClick={() => setExpanded((e) => !e)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            textAlign: 'left',
            flex: '1 1 280px',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {expanded ? '▾' : '▸'} {item.title}
          </span>
          {item.qcCaseNumber && item.qcCaseId && (
            <Link
              to={`/cases/${item.qcCaseId}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                marginLeft: 8,
                fontSize: 12,
                fontWeight: 700,
                color: '#C53030',
                textDecoration: 'none',
                background: '#FFF5F5',
                border: '1px solid #FEB2B2',
                borderRadius: 4,
                padding: '1px 6px',
              }}
            >
              {item.qcCaseNumber}
            </Link>
          )}
        </button>
        <div style={{ display: 'flex', gap: 6 }}>
          {(Object.keys(RESULT_STYLES) as QcAuditResult[]).map((r) => {
            const active = current === r;
            const s = RESULT_STYLES[r];
            return (
              <button
                key={r}
                onClick={() => pick(active ? null : r)}
                disabled={disabled || saving}
                title={active ? 'Click again to clear' : s.label}
                style={{
                  border: `1px solid ${active ? s.fg : '#CBD5E0'}`,
                  background: active ? s.bg : '#fff',
                  color: active ? s.fg : '#5A6B7C',
                  borderRadius: 6,
                  padding: '5px 12px',
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: disabled || saving ? 'default' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {expanded && (
        <div style={{ margin: '8px 0 0 18px', display: 'grid', gap: 8, fontSize: 13 }}>
          <div>
            <span style={{ fontWeight: 700, color: '#001C3E' }}>How to test: </span>
            {item.howToTest}
          </div>
          <div>
            <span style={{ fontWeight: 700, color: '#001C3E' }}>Expected: </span>
            {item.expected}
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => {
              // Persist a note edit against the existing result (no-op when
              // nothing recorded yet — the note rides the next result click).
              if (item.result && note.trim() !== (item.note ?? '')) void onResult(item, item.result, note);
            }}
            placeholder="Notes / what you observed (required context on a fail — it becomes the case's Observed section)"
            disabled={disabled}
            rows={2}
            style={{
              width: '100%',
              border: '1px solid #CBD5E0',
              borderRadius: 6,
              padding: 8,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>
      )}
    </div>
  );
}

export function AuditRunPage() {
  const { runId } = useParams();
  const getToken = useApiToken();
  const me = useMe();
  const [run, setRun] = useState<QcAuditRunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);

  const id = Number(runId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const detail = await auditApi.getRun(token, id);
        if (!cancelled) setRun(detail);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load run');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, id]);

  const sections = useMemo(() => {
    if (!run) return [];
    const bySection = new Map<string, QcAuditRunItem[]>();
    for (const item of run.items) {
      const list = bySection.get(item.section) ?? [];
      list.push(item);
      bySection.set(item.section, list);
    }
    return [...bySection.entries()];
  }, [run]);

  async function handleResult(item: QcAuditRunItem, result: QcAuditResult | null, note: string) {
    try {
      const token = await getToken();
      const updated = await auditApi.setItemResult(token, id, item.runItemId, result, note.trim() || undefined);
      setRun(updated);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save result');
    }
  }

  async function finish(abandoned: boolean) {
    if (!run) return;
    if (
      !abandoned &&
      run.pendingCount > 0 &&
      !window.confirm(`${run.pendingCount} item(s) are still pending. Complete the run anyway?`)
    ) {
      return;
    }
    setFinishing(true);
    try {
      const token = await getToken();
      setRun(await auditApi.completeRun(token, id, abandoned));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete run');
    } finally {
      setFinishing(false);
    }
  }

  if (error && !run) return <div style={{ padding: 24, color: '#C53030' }}>{error}</div>;
  if (!run) return <div style={{ padding: 24, color: '#5A6B7C' }}>Loading run…</div>;

  const done = run.passCount + run.failCount + run.blockedCount;
  const readOnly = run.status !== 'in_progress';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gap: 14 }}>
      <div>
        <Link to="/audits" style={{ fontSize: 13, color: '#0B5FA5', textDecoration: 'none' }}>
          ← Audits
        </Link>
        <h1 style={{ fontSize: 20, margin: '6px 0 2px' }}>
          {run.templateName} v{run.templateVersion} — run #{run.id}
        </h1>
        <div style={{ fontSize: 13, color: '#5A6B7C' }}>
          {run.moduleName} · target: <strong>{run.targetEnvironment}</strong> · started{' '}
          {relativeTime(run.startedAt)} by {personLabel(run.startedBy, me?.oid ?? '', run.people, { selfName: me?.displayName })}
          {run.completedAt && <> · finished {relativeTime(run.completedAt)}</>}
        </div>
      </div>

      {/* Sticky progress header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: '#fff',
          border: '1px solid #E2E8F0',
          borderRadius: 10,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 180, height: 8, background: '#EDF2F7', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: `${(run.passCount / Math.max(run.totalItems, 1)) * 100}%`, background: '#2F855A' }} />
          <div style={{ width: `${(run.failCount / Math.max(run.totalItems, 1)) * 100}%`, background: '#C53030' }} />
          <div style={{ width: `${(run.blockedCount / Math.max(run.totalItems, 1)) * 100}%`, background: '#B7791F' }} />
        </div>
        <span style={{ fontSize: 13, color: '#5A6B7C' }}>
          {done}/{run.totalItems} · ✓ {run.passCount} · ✗ {run.failCount} · ⊘ {run.blockedCount}
        </span>
        {/* Never let a shortened checklist read as full coverage. */}
        {run.itemsExcludedForRole > 0 && (
          <span
            title="These items need permissions you don't hold in the module being audited, so they were left off your checklist rather than handed to you to fail."
            style={{
              fontSize: 12,
              color: '#5A6B7C',
              background: '#EDF2F7',
              border: '1px solid #E2E8F0',
              borderRadius: 999,
              padding: '2px 10px',
            }}
          >
            {run.itemsExcludedForRole} not shown for your role
          </span>
        )}
        {!readOnly && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => finish(false)}
              disabled={finishing}
              style={{
                background: '#001C3E',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {finishing ? 'Finishing…' : 'Complete run'}
            </button>
            <button
              onClick={() => finish(true)}
              disabled={finishing}
              style={{
                background: 'none',
                color: '#5A6B7C',
                border: '1px solid #CBD5E0',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Abandon
            </button>
          </div>
        )}
        {readOnly && (
          <span style={{ fontSize: 12.5, fontWeight: 700, color: run.status === 'completed' ? '#0B6B3A' : '#5A6B7C' }}>
            {run.status === 'completed' ? '✓ Completed' : 'Abandoned'}
          </span>
        )}
      </div>

      {error && (
        <div style={{ background: '#FFF5F5', border: '1px solid #FEB2B2', borderRadius: 8, padding: 10, color: '#C53030', fontSize: 13 }}>
          {error}
        </div>
      )}

      {sections.map(([section, items]) => {
        const sectionDone = items.filter((i) => i.result !== null).length;
        return (
          <div key={section} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <h2 style={{ fontSize: 14.5, margin: 0, color: '#001C3E' }}>{section}</h2>
              <span style={{ fontSize: 12, color: '#5A6B7C' }}>
                {sectionDone}/{items.length}
              </span>
            </div>
            {items.map((item) => (
              <ItemRow key={item.runItemId} item={item} disabled={readOnly} onResult={handleResult} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
